import React, { useState, useMemo, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Chip,
  Box,
  TextField,
  InputAdornment,
  Button,
  Typography,
  Alert,
  CircularProgress,
  SelectChangeEvent
} from '@mui/material';
import {
  Search as SearchIcon,
  SelectAll as SelectAllIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { Property, PropertySelectorProps } from '../../types';
import { validatePropertySelection } from './validation';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export const PropertySelector: React.FC<PropertySelectorProps> = ({
  properties,
  selectedProperties,
  onSelectionChange,
  loading = false,
  error
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Filter properties based on search term
  const filteredProperties = useMemo(() => {
    if (!searchTerm) return properties;
    return properties.filter(property =>
      property.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      property.address.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [properties, searchTerm]);

  // Get active properties only
  const activeProperties = useMemo(() => {
    return filteredProperties.filter(property => property.status === 'active');
  }, [filteredProperties]);

  // Validate selection whenever it changes
  useEffect(() => {
    const validation = validatePropertySelection(properties, selectedProperties);
    setValidationErrors(validation.errors);
  }, [properties, selectedProperties]);

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    const selectedIds = typeof value === 'string' ? value.split(',') : value;
    
    // Validate that all selected properties exist and are active
    const validIds = selectedIds.filter(id => 
      activeProperties.some(property => property.id === id)
    );
    
    onSelectionChange(validIds);
  };

  const handleSelectAll = () => {
    const allActiveIds = activeProperties.map(property => property.id);
    onSelectionChange(allActiveIds);
  };

  const handleClearAll = () => {
    onSelectionChange([]);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const getSelectedPropertyNames = () => {
    return selectedProperties
      .map(id => properties.find(property => property.id === id)?.name)
      .filter(Boolean)
      .join(', ');
  };

  const renderValue = (selected: string[]) => {
    if (selected.length === 0) {
      return <Typography color="textSecondary">Select properties</Typography>;
    }
    
    if (selected.length === 1) {
      const property = properties.find(p => p.id === selected[0]);
      return property?.name || 'Unknown Property';
    }
    
    if (selected.length <= 3) {
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {selected.map((id) => {
            const property = properties.find(p => p.id === id);
            return property ? (
              <Chip
                key={id}
                label={property.name}
                size="small"
                variant="outlined"
              />
            ) : null;
          })}
        </Box>
      );
    }
    
    return `${selected.length} properties selected`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={20} />
        <Typography>Loading properties...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (properties.length === 0) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No properties available. Please add properties to view financial data.
      </Alert>
    );
  }

  return (
    <Box>
      <FormControl fullWidth variant="outlined">
        <InputLabel id="property-selector-label">Properties</InputLabel>
        <Select
          labelId="property-selector-label"
          id="property-selector"
          multiple
          value={selectedProperties}
          onChange={handleChange}
          input={<OutlinedInput label="Properties" />}
          renderValue={renderValue}
          MenuProps={MenuProps}
          open={isOpen}
          onOpen={() => setIsOpen(true)}
          onClose={() => setIsOpen(false)}
        >
          {/* Search field */}
          <Box sx={{ p: 1, borderBottom: '1px solid #e0e0e0' }}>
            <TextField
              size="small"
              placeholder="Search properties..."
              value={searchTerm}
              onChange={handleSearchChange}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </Box>

          {/* Select All / Clear All buttons */}
          <Box sx={{ p: 1, display: 'flex', gap: 1, borderBottom: '1px solid #e0e0e0' }}>
            <Button
              size="small"
              startIcon={<SelectAllIcon />}
              onClick={(e) => {
                e.stopPropagation();
                handleSelectAll();
              }}
              disabled={selectedProperties.length === activeProperties.length}
            >
              Select All
            </Button>
            <Button
              size="small"
              startIcon={<ClearIcon />}
              onClick={(e) => {
                e.stopPropagation();
                handleClearAll();
              }}
              disabled={selectedProperties.length === 0}
            >
              Clear All
            </Button>
          </Box>

          {/* Property list */}
          {activeProperties.length === 0 ? (
            <MenuItem disabled>
              <Typography color="textSecondary">
                {searchTerm ? 'No properties match your search' : 'No active properties available'}
              </Typography>
            </MenuItem>
          ) : (
            activeProperties.map((property) => (
              <MenuItem key={property.id} value={property.id}>
                <Checkbox checked={selectedProperties.includes(property.id)} />
                <ListItemText
                  primary={property.name}
                  secondary={property.address}
                  secondaryTypographyProps={{
                    fontSize: '0.75rem',
                    color: 'textSecondary'
                  }}
                />
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>

      {/* Validation errors */}
      {validationErrors.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
            {validationErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Selected properties summary */}
      {selectedProperties.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="textSecondary">
            {selectedProperties.length} of {activeProperties.length} properties selected
          </Typography>
        </Box>
      )}
    </Box>
  );
};